import {
    buildStaticRuleSetDisplays,
    computeRuleSetDisplays,
} from '@fruitful/conversational-mm';
import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    getMinSpendCents,
    MIN_GOALS_CENTS,
    MoneyMapVisual,
} from '@fruitful/money-map';
import { buildAhaResult, buildSimulation } from '@fruitful/money-map-simulator';
import {
    AnalyticsUserEvent,
    type OnboardingChatResults,
    ResultSlide,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    ArrowLeft,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    IconButton,
    isNonMobileWeb,
    isWeb,
    Spinner,
    SquarePenIcon,
    TriangleAlert,
    useThemeColors,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { refreshMoneyMapsAfterReviewUpdate } from '../../banking/hooks/income-split/invalidateIncomeSplitAndMoneyMapQueries';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException, track } from '../../logger/Logger';
import { useGetConvMmThread } from '../hooks/useGetConvMmThread';
import useUpdateMoneyMapFromReview from '../hooks/useUpdateMoneyMapFromReview';
import { confirmedReviewResultsAtom } from '../state/OnboardingAtoms';
import {
    type BillItem,
    type DebtItem,
    EditBillsSheet,
    EditDebtsSheet,
    EditIncomeSheet,
    EditSpendGoalsSheet,
    rebalanceAfterIncomeChange,
} from './EditMoneyMapSheets';
import GuideAvatars from './GuideAvatars';
type EditSheet = 'income' | 'bills' | 'spend' | 'goals' | 'debts' | null;

const OptionImage = ({ children }: { children: React.ReactNode }) => {
    const { getColor } = useThemeColors();

    return (
        <LinearGradient
            colors={[getColor('$peachLight'), getColor('$peach')]}
            locations={[0.1479, 0.9608]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{ padding: 6, borderRadius: 134 }}
        >
            {children}
        </LinearGradient>
    );
};

const OnboardingReviewNumbers = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`Onboarding/ReviewNumbers`);

    const { getColor } = useThemeColors();
    const { top: safeTop, bottom: safeBottom } = useSafeAreaInsets();
    const paddingTop = isWeb ? 24 : safeTop + 24;

    const { data: convMmThread, isLoading } = useGetConvMmThread({});

    const [results, setResults] = useState<OnboardingChatResults | null>(null);
    const setConfirmedResults = useSetAtom(confirmedReviewResultsAtom);

    const queryClient = useQueryClient();
    const toast = useToastController();
    const { mutateAsync: updateMoneyMapFromReview } =
        useUpdateMoneyMapFromReview();
    const [isSaving, setIsSaving] = useState(false);

    const [animateVisual, setAnimateVisual] = useState(false);

    useEffect(() => {
        if (convMmThread?.results != null && results == null) {
            setResults(
                JSON.parse(
                    JSON.stringify(convMmThread.results),
                ) as OnboardingChatResults,
            );
            requestAnimationFrame(() => {
                setAnimateVisual(true);
            });
        }
    }, [convMmThread?.results, results]);

    const income = results?.moneyMap?.incomeTotalCents ?? 0;
    const bills = results?.moneyMap?.billsTotalCents ?? 0;
    const spend = results?.moneyMap?.spendTotalCents ?? 0;
    const goals = results?.moneyMap?.goalsTotalCents ?? 0;

    const billItems: BillItem[] = useMemo(
        () =>
            (results?.rawChatResults?.bills ?? []).map((bill, index) => ({
                id: `bill-${index}`,
                name: bill.label,
                amountCents: bill.amountCents,
            })),
        [results?.rawChatResults?.bills],
    );

    const debtItems: DebtItem[] = useMemo(() => {
        const existing = (results?.rawChatResults?.debts ?? []).map(
            (debt, index) => ({
                id: `debt-${index}`,
                name: debt.description,
                balanceCents: debt.balanceRemainingCents,
            }),
        );
        const defaults: DebtItem[] = [
            {
                id: 'default-credit-cards',
                name: 'Credit cards',
                balanceCents: 0,
            },
            {
                id: 'default-personal-loans',
                name: 'Personal loans',
                balanceCents: 0,
            },
        ];
        const existingNames = new Set(
            existing.map((d) => d.name.toLowerCase()),
        );
        const missing = defaults.filter(
            (d) => !existingNames.has(d.name.toLowerCase()),
        );
        return [...existing, ...missing];
    }, [results?.rawChatResults?.debts]);

    const [editSheet, setEditSheet] = useState<EditSheet>(null);

    const [isValid, setIsValid] = useState(true);
    const handleValidityChange = useCallback((valid: boolean) => {
        setIsValid(valid);
    }, []);

    const handleEditRequest = useCallback(
        (category: 'income' | 'bills' | 'spend' | 'goals') => {
            console.log('handleEditRequest', category);
            track(AnalyticsUserEvent.ONBOARDING_GOYO_OPEN_SHEET, {
                category,
            });
            setEditSheet(category);
        },
        [],
    );

    const updateMoneyMap = useCallback(
        (updates: Partial<OnboardingChatResults['moneyMap']>) => {
            setResults((prev) => {
                if (prev == null) return prev;
                return {
                    ...prev,
                    moneyMap: { ...prev.moneyMap, ...updates },
                };
            });
        },
        [],
    );

    const handleIncomeSave = useCallback(
        (cents: number) => {
            track(AnalyticsUserEvent.ONBOARDING_GOYO_EDITED, {
                category: 'income',
            });
            const { newSpend, newGoals } = rebalanceAfterIncomeChange(
                cents,
                bills,
                spend,
                goals,
                income,
            );
            updateMoneyMap({
                incomeTotalCents: cents,
                spendTotalCents: newSpend,
                goalsTotalCents: newGoals,
            });
        },
        [bills, spend, goals, income, updateMoneyMap],
    );

    const handleSpendGoalsSave = useCallback(
        (newSpend: number, newGoals: number) => {
            track(AnalyticsUserEvent.ONBOARDING_GOYO_EDITED, {
                category: editSheet === 'spend' ? 'spend' : 'goals',
            });
            updateMoneyMap({
                spendTotalCents: newSpend,
                goalsTotalCents: newGoals,
            });
        },
        [editSheet, updateMoneyMap],
    );

    const handleBillsSave = useCallback(
        (updatedBills: BillItem[]) => {
            track(AnalyticsUserEvent.ONBOARDING_GOYO_EDITED, {
                category: 'bills',
            });
            const newBillsTotal = updatedBills.reduce(
                (sum, b) => sum + b.amountCents,
                0,
            );
            const afterBills = income - newBillsTotal;
            const minSpend = getMinSpendCents(income);
            let newSpend = Math.min(spend, afterBills);
            let newGoals = Math.max(0, afterBills - newSpend);
            if (newSpend < minSpend) {
                newSpend = minSpend;
                newGoals = Math.max(0, afterBills - newSpend);
            }
            if (newGoals < MIN_GOALS_CENTS) {
                newGoals = MIN_GOALS_CENTS;
                newSpend = Math.max(minSpend, afterBills - newGoals);
            }
            setResults((prev) => {
                if (prev == null) return prev;
                return {
                    ...prev,
                    moneyMap: {
                        ...prev.moneyMap,
                        billsTotalCents: newBillsTotal,
                        spendTotalCents: newSpend,
                        goalsTotalCents: newGoals,
                    },
                    rawChatResults: {
                        ...prev.rawChatResults,
                        bills: updatedBills.map((b, i) => ({
                            ...(prev.rawChatResults.bills?.[i] ?? {
                                label: b.name,
                                category: 'other',
                            }),
                            amountCents: b.amountCents,
                        })),
                    },
                };
            });
        },
        [income, spend],
    );

    const handleDebtsSave = useCallback((updatedDebts: DebtItem[]) => {
        track(AnalyticsUserEvent.ONBOARDING_GOYO_EDITED, {
            category: 'debts',
        });
        setResults((prev) => {
            if (prev == null) return prev;
            return {
                ...prev,
                rawChatResults: {
                    ...prev.rawChatResults,
                    debts: updatedDebts.map((d, i) => ({
                        ...(prev.rawChatResults.debts?.[i] ?? {
                            description: d.name,
                            minimumPaymentCents: 0,
                            annualInterestRatePercent: 0,
                        }),
                        balanceRemainingCents: d.balanceCents,
                    })),
                },
            };
        });
    }, []);

    const debtsTotalCents = debtItems.reduce(
        (sum, d) => sum + d.balanceCents,
        0,
    );

    const VISUAL_NATURAL_HEIGHT = 520;
    const [middleSectionHeight, setMiddleSectionHeight] = useState(0);
    const [debtsBoxHeight, setDebtsBoxHeight] = useState(0);
    const [visualContainerWidth, setVisualContainerWidth] = useState(0);
    const GAP = 12;
    const availableVisualHeight =
        middleSectionHeight > 0
            ? middleSectionHeight -
              debtsBoxHeight -
              (debtsBoxHeight > 0 ? GAP : 0)
            : 0;
    const visualScale =
        availableVisualHeight > 0
            ? Math.min(1, availableVisualHeight / VISUAL_NATURAL_HEIGHT)
            : 1;

    const headerContent = (
        <>
            <XStack
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                position="relative"
            >
                <IconButton
                    variant={ButtonVariant.SECONDARY}
                    icon={ArrowLeft}
                    onPress={() => {
                        router.canGoBack()
                            ? router.back()
                            : router.replace('/');
                    }}
                />
                <BodyLargeRegular
                    position="absolute"
                    left={0}
                    right={0}
                    textAlign="center"
                    pointerEvents="none"
                >
                    Review
                </BodyLargeRegular>
                <Pressable
                    onPress={() => {
                        router.navigate('/onboarding/book-first-session');
                    }}
                >
                    <OptionImage>
                        <GuideAvatars size="small" />
                    </OptionImage>
                </Pressable>
            </XStack>
            <BodyMediumRegular color="$gray1000" textAlign="center">
                First, let&apos;s double check your monthly numbers
            </BodyMediumRegular>
        </>
    );

    const visualContent = isLoading ? (
        <Spinner />
    ) : isNonMobileWeb ? (
        <MoneyMapVisual
            mode="go-your-own-way"
            incomeTotalCents={income}
            billsTotalCents={bills}
            spendTotalCents={spend}
            goalsTotalCents={goals}
            onValidityChange={handleValidityChange}
            onEditRequest={handleEditRequest}
            maxWidth={500}
            animated={animateVisual}
        />
    ) : (
        <View
            width="100%"
            height={VISUAL_NATURAL_HEIGHT * visualScale}
            onLayout={(e) => {
                setVisualContainerWidth(e.nativeEvent.layout.width);
            }}
        >
            <View
                style={{
                    transform: [{ scale: visualScale }],
                    transformOrigin: 'top center',
                    width:
                        visualScale < 1 && visualContainerWidth > 0
                            ? visualContainerWidth / visualScale
                            : '100%',
                    alignSelf: 'center',
                    height: VISUAL_NATURAL_HEIGHT,
                }}
            >
                <MoneyMapVisual
                    mode="go-your-own-way"
                    incomeTotalCents={income}
                    billsTotalCents={bills}
                    spendTotalCents={spend}
                    goalsTotalCents={goals}
                    onValidityChange={handleValidityChange}
                    onEditRequest={handleEditRequest}
                    maxWidth={500}
                    animated={animateVisual}
                />
            </View>
        </View>
    );

    const debtsContent = !isLoading && (
        <View
            width="100%"
            position="relative"
            onLayout={(e) => {
                setDebtsBoxHeight(e.nativeEvent.layout.height);
            }}
        >
            <Pressable
                onPress={() => {
                    setEditSheet('debts');
                }}
                style={{ cursor: 'pointer' }}
            >
                <YStack
                    width="100%"
                    backgroundColor={
                        debtsTotalCents > 0 ? '$peach' : '$gray200'
                    }
                    borderRadius={16}
                    paddingVertical={12}
                    paddingHorizontal={16}
                    gap={4}
                    alignItems="center"
                >
                    <BodyMediumRegular
                        color={
                            debtsTotalCents > 0 ? '$peachDarkest' : '$gray1000'
                        }
                    >
                        {centsToFormattedDollars(debtsTotalCents, false)}
                    </BodyMediumRegular>
                    <BodyMediumRegular
                        color={
                            debtsTotalCents > 0 ? '$peachDarkest' : '$gray1000'
                        }
                    >
                        High-interest debt
                    </BodyMediumRegular>
                </YStack>
            </Pressable>
            <Pressable
                onPress={() => {
                    setEditSheet('debts');
                }}
                accessibilityRole="button"
                accessibilityLabel="Edit debts"
                style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: getColor(
                        debtsTotalCents > 0 ? '$peachLightest' : '$gray400',
                    ),
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                }}
            >
                <Icon
                    icon={SquarePenIcon}
                    size={16}
                    color={debtsTotalCents > 0 ? '$peachDarkest' : '$gray1000'}
                />
            </Pressable>
        </View>
    );

    const buttonContent = !isLoading && (
        <YStack gap={16} width="100%" alignItems="center">
            {!isValid && (
                <XStack alignItems="center" gap={8}>
                    <Icon icon={TriangleAlert} size={16} color="$orange1100" />
                    <BodySmallRegular color="$orange1100" textAlign="center">
                        Adjust income or bills to continue
                    </BodySmallRegular>
                </XStack>
            )}
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                onPress={() => {
                    if (results == null) {
                        return;
                    }

                    track(AnalyticsUserEvent.ONBOARDING_GOYO_LOOKS_GOOD);

                    const ahaResult = buildAhaResult(results);

                    const simulation = buildSimulation({
                        ...results.rawChatResults,
                        monthlySpendCents: spend,
                        monthlyGoalsSpendCents: goals,
                    });

                    const ruleSetDisplays =
                        simulation != null && goals > 0
                            ? computeRuleSetDisplays(simulation).ruleSets
                            : buildStaticRuleSetDisplays(
                                  results.rawChatResults,
                              );

                    const updatedResults: OnboardingChatResults = {
                        ...results,
                        artifactData: {
                            ...results.artifactData,
                            debtFreeDate:
                                ahaResult.type !== 'investing'
                                    ? ahaResult.debtFreeDate
                                    : results.artifactData.debtFreeDate,
                        },
                        goalCompletionDates: {
                            ...results.goalCompletionDates,
                            [ResultSlide.Goals]: ruleSetDisplays,
                        },
                    };

                    void (async () => {
                        setIsSaving(true);
                        try {
                            // Persist the reviewed numbers here so the next
                            // screen is just a confirmation (it no longer
                            // saves on its "Next" button).
                            await updateMoneyMapFromReview({
                                body: updatedResults,
                            });
                            await refreshMoneyMapsAfterReviewUpdate(
                                queryClient,
                            );
                            setConfirmedResults(updatedResults);
                            router.push('/onboarding/confirm');
                        } catch (error) {
                            captureException(error);
                            toast.show(
                                'Could not update your Money Map. Try again.',
                            );
                        } finally {
                            setIsSaving(false);
                        }
                    })();
                }}
                text="Looks good"
                disabled={!isValid || isSaving}
                width="full"
            />
        </YStack>
    );

    return (
        <YStack width="100%" flex={1} maxWidth={500} alignSelf="center">
            {isNonMobileWeb ? (
                <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{
                        alignItems: 'center',
                        paddingTop,
                        paddingHorizontal: 24,
                        paddingBottom: 24,
                        gap: 12,
                    }}
                    showsVerticalScrollIndicator
                >
                    {headerContent}
                    {visualContent}
                    {debtsContent}
                    {buttonContent}
                </ScrollView>
            ) : (
                <YStack
                    flex={1}
                    alignItems="center"
                    paddingTop={paddingTop}
                    paddingHorizontal={24}
                    paddingBottom={safeBottom || 12}
                    gap={12}
                >
                    {headerContent}
                    <YStack
                        flex={1}
                        width="100%"
                        alignItems="center"
                        gap={12}
                        onLayout={(e) => {
                            setMiddleSectionHeight(e.nativeEvent.layout.height);
                        }}
                    >
                        {visualContent}
                        {debtsContent}
                    </YStack>
                    {buttonContent}
                </YStack>
            )}

            <EditIncomeSheet
                isOpen={editSheet === 'income'}
                onClose={() => {
                    setEditSheet(null);
                }}
                incomeTotalCents={income}
                billsTotalCents={bills}
                spendTotalCents={spend}
                goalsTotalCents={goals}
                onSave={handleIncomeSave}
            />
            <EditSpendGoalsSheet
                isOpen={editSheet === 'spend' || editSheet === 'goals'}
                onClose={() => {
                    setEditSheet(null);
                }}
                title={editSheet === 'spend' ? 'Spend' : 'Goals'}
                description={
                    editSheet === 'spend'
                        ? 'This should be your daily spend like groceries, shopping, parking, restaurants, etc.'
                        : 'This is what you have left over after bills and spend every month'
                }
                incomeTotalCents={income}
                billsTotalCents={bills}
                spendTotalCents={spend}
                onSave={handleSpendGoalsSave}
            />
            <EditBillsSheet
                isOpen={editSheet === 'bills'}
                onClose={() => {
                    setEditSheet(null);
                }}
                billItems={billItems}
                incomeTotalCents={income}
                spendTotalCents={spend}
                goalsTotalCents={goals}
                onSave={handleBillsSave}
            />
            <EditDebtsSheet
                isOpen={editSheet === 'debts'}
                onClose={() => {
                    setEditSheet(null);
                }}
                debtItems={debtItems}
                onSave={handleDebtsSave}
            />
        </YStack>
    );
};

export default OnboardingReviewNumbers;
